<script setup lang="ts">
/**
 * Une ligne de la liste des surveillances (`rendreListe`, S:5196-5230), en
 * lecture : l'état, l'équipement et son lieu, le type d'alarme, la dernière
 * mesure datée de l'alarme en cours, et ce que dit l'état.
 *
 * Aucun bouton d'écriture dans ce lot (matrice, gestes 1, 3 et 51). Aucune
 * mesure n'est lue pour rendre la ligne : la valeur est celle de l'alarme.
 *
 * Lot F3 : le nom de l'équipement ouvre la fiche de la surveillance (S:1459,
 * « on y arrive par le nom de l'équipement dans la liste »), sous la fiche
 * de son équipement. Hors routeur (tests, stories), le nom reste un texte.
 *
 * Lot F6 : « Acquitter » sur la ligne d'une alarme à traiter active (geste
 * 4, `BoutonAcquitter`), puis la liste est relue par `relire`.
 */
// ================================
// IMPORTS
// ================================
import { computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { routerKey } from "vue-router"
import { cheminDeLaFiche } from "@modules/alarmes-v2/utils/menu"
import { useTypesStore } from "@modules/alarmes-v2/stores/types.store"
import { etatDeLaLigne, type EtatLigne } from "@modules/alarmes-v2/utils/etats"
import {
    formaterInstant,
    formaterMesure,
} from "@modules/alarmes-v2/utils/mesure"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type {
    AlarmesV2Alarme,
    AlarmesV2Personne,
    AlarmesV2Surveillance,
} from "@modules/alarmes-v2/types/alarmes-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// TYPES & INTERFACES
// ================================
/** La cellule « Dernière mesure » : la valeur, sa date, ou l'absence dite. */
interface CelluleMesure {
    principal: string
    secondaire: string
    absente: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** La surveillance de la ligne. */
        surveillance: AlarmesV2Surveillance
        /** Son alarme ouverte, ou `null`. */
        alarme: AlarmesV2Alarme | null
        /** Les noms servis avec les alarmes. */
        personnes?: Record<string, AlarmesV2Personne>
        /** Relit la liste après un acquittement ; vrai si elle a abouti. */
        relire?: () => Promise<boolean>
    }>(),
    { personnes: () => ({}), relire: () => Promise.resolve(false) }
)

/**
 * Le catalogue, pour le nom du type d'alarme.
 */
const typesStore = useTypesStore()

/**
 * L'état de la ligne, en toutes lettres.
 */
const getEtat = computed(
    (): EtatLigne =>
        etatDeLaLigne(
            props.surveillance,
            props.alarme,
            t as unknown as Traduire,
            props.personnes
        )
)

/**
 * Le nom du type d'alarme, ou la phrase qui dit qu'il n'est pas au catalogue.
 * Jamais le code du type.
 */
const getNomDuType = computed(
    (): string =>
        typesStore.nomDuType(
            props.surveillance.type_code,
            props.surveillance.type_version
        ) ?? t("alarmesV2.liste.typeNonDisponible")
)

/**
 * Le nom de l'équipement.
 */
const getNomEquipement = computed(
    (): string =>
        props.surveillance.objet?.nom || t("alarmesV2.liste.equipementSansNom")
)

/**
 * Le routeur, absent hors application (tests, stories).
 */
const routeur = inject(routerKey, null)

/**
 * Le chemin de la fiche, ou `null` sans routeur ou sans équipement servi.
 */
const getLienFiche = computed((): string | null =>
    routeur ? cheminDeLaFiche(props.surveillance) : null
)

/**
 * Le lieu de l'équipement, « — » s'il n'est pas servi.
 */
const getLieu = computed(
    (): string =>
        props.surveillance.objet?.site_nom || t("alarmesV2.mesure.absente")
)

/**
 * La dernière mesure de l'alarme en cours (`ligneValeur`, S:5168-5178).
 */
const getMesure = computed((): CelluleMesure => {
    const a = props.alarme
    const absente = {
        principal: t("alarmesV2.mesure.absente"),
        secondaire: "",
        absente: true,
    }
    if (!a) return absente
    const quand = formaterInstant(
        a.derniere_valeur_le,
        t as unknown as Traduire
    )
    if (a.motif_ouverture === "silence_sonde") {
        return {
            principal: t("alarmesV2.mesure.aucuneMesure"),
            secondaire: quand
                ? t("alarmesV2.mesure.derniereMesure", { quand })
                : "",
            absente: false,
        }
    }
    if (typeof a.derniere_valeur_constatee !== "number") return absente
    return {
        principal: formaterMesure(
            a.derniere_valeur_constatee,
            a.seuils_figes?.unite,
            t as unknown as Traduire
        ),
        secondaire: quand ?? "",
        absente: false,
    }
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <tr class="border-t border-surface-200 align-top" data-test="ligne">
        <td class="px-3 py-2">
            <EtatSurveillance :etat="getEtat" />
        </td>
        <td class="px-3 py-2">
            <RouterLink
                v-if="getLienFiche"
                :to="getLienFiche"
                class="block text-sm font-medium text-primary-700 underline"
                :title="t('alarmesV2.equipement.ouvrirLaFiche')"
                data-test="lien-fiche"
                >{{ getNomEquipement }}</RouterLink
            >
            <span v-else class="block text-sm font-medium text-surface-900">{{
                getNomEquipement
            }}</span>
            <span class="block text-xs text-surface-600">{{ getLieu }}</span>
        </td>
        <td class="px-3 py-2 text-sm text-surface-800">{{ getNomDuType }}</td>
        <td class="px-3 py-2">
            <span
                class="block text-sm"
                :class="
                    getMesure.absente ? 'text-surface-400' : 'text-surface-900'
                "
            >
                {{ getMesure.principal }}
                <span v-if="getMesure.absente" class="sr-only">{{
                    t("alarmesV2.mesure.absenteRaison")
                }}</span>
            </span>
            <span
                v-if="getMesure.secondaire"
                class="block text-xs text-surface-600"
                >{{ getMesure.secondaire }}</span
            >
        </td>
        <td class="px-3 py-2">
            <span class="block text-sm text-surface-800">{{
                getEtat.detail
            }}</span>
            <span
                v-if="getEtat.second"
                class="block text-xs text-surface-600"
                >{{ getEtat.second }}</span
            >
        </td>
        <td class="px-3 py-2">
            <BoutonAcquitter
                :alarme="alarme"
                :surveillance="surveillance"
                :relire="relire"
                :avec-aide="false"
                classe="rounded border border-primary-600 bg-primary-600 px-2 py-1 text-xs font-medium text-white"
            />
        </td>
    </tr>
</template>
